/**
 * Accept Family Invitation page
 * Handles family invitation acceptance via token
 */

import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import familyService from '../services/familyService';

const AcceptFamilyInvitation = () => {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useAppNavigation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [familyInfo, setFamilyInfo] = useState(null);

  useEffect(() => {
    // Get token from URL params or query string
    const urlToken = token || new URLSearchParams(location.search).get('token');
    
    if (urlToken) {
      acceptInvitation(urlToken);
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token, location]);

  const acceptInvitation = async (invitationToken) => {
    try {
      setLoading(true);
      console.log('Attempting to accept invitation with token:', invitationToken);
      const response = await familyService.acceptInvitation(invitationToken);
      console.log('Invitation response:', response);
      
      if (response.success) {
        console.log('Invitation accepted successfully');
        setSuccess(true);
        setFamilyInfo(response);
        
        // Redirect to Pro Features page after 3 seconds
        setTimeout(() => {
          navigate.to.PRO_FEATURES();
        }, 3000);
      } else {
        console.log('Invitation failed:', response.error);
        setError(response.error || 'Failed to accept invitation');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {success ? (
            <>
              <Typography variant="h4" gutterBottom color="success.main">
                ✅ Invitation Accepted!
              </Typography>
              <Typography variant="body1" paragraph>
                You've successfully joined the family group!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Redirecting to Pro Features...
              </Typography>
            </>
          ) : error ? (
            <>
              <Typography variant="h4" gutterBottom color="error.main">
                ❌ Invitation Failed
              </Typography>
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate.to.HOME()}
              >
                Go to Dashboard
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h4" gutterBottom>
                Accept Family Invitation
              </Typography>
              <Typography variant="body1" paragraph>
                Processing your invitation...
              </Typography>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AcceptFamilyInvitation;
